/**
 * CDB54 Calendrier+ - Auth Store
 * Pinia store for authentication state management
 * NFR-P10: Refresh token transparent sans interruption UX
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { User, LoginCredentials, AuthResponse, ApiError } from '@/types/models';
import api, { initCsrf } from '@/services/api';
import type { AxiosError } from 'axios';

// Session refresh interval: 6 hours (refresh well before 24h expiration warning)
const REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000;

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const retryAfter = ref<number | null>(null);
  const sessionExpiresAt = ref<string | null>(null);

  // Session refresh timer
  let refreshTimer: ReturnType<typeof setInterval> | null = null;

  // Getters
  const isAuthenticated = computed(() => user.value !== null);
  const fullName = computed(() => user.value?.full_name ?? `${user.value?.first_name} ${user.value?.last_name}`);
  const userRole = computed(() => user.value?.role);
  const isAdmin = computed(() => user.value?.role === 'admin');
  const isResponsableSportif = computed(() =>
    user.value?.role === 'admin' || user.value?.role === 'responsable_sportif'
  );

  /**
   * Check if user has a specific permission
   */
  function hasPermission(permission: string): boolean {
    return user.value?.permissions?.includes(permission) ?? false;
  }

  /**
   * Login with licence number and birth date
   */
  async function login(credentials: LoginCredentials): Promise<boolean> {
    loading.value = true;
    error.value = null;
    retryAfter.value = null;

    try {
      // Initialize CSRF protection
      await initCsrf();

      const response = await api.post<AuthResponse>('/auth/login', credentials);
      user.value = response.data.data;
      initSession();
      return true;
    } catch (err) {
      const axiosError = err as AxiosError<ApiError>;

      if (axiosError.response?.status === 429) {
        // Rate limited
        retryAfter.value = axiosError.response.data?.retry_after ?? 60;
        error.value = axiosError.response.data?.message ?? 'Trop de tentatives. Veuillez patienter.';
      } else if (axiosError.response?.status === 422) {
        // Validation error
        const errors = axiosError.response.data?.errors;
        if (errors) {
          error.value = Object.values(errors).flat().join(' ');
        } else {
          error.value = axiosError.response.data?.message ?? 'Erreur de validation';
        }
      } else if (axiosError.response?.status === 401) {
        error.value = 'Identifiants incorrects';
      } else {
        error.value = 'Une erreur est survenue. Veuillez réessayer.';
      }
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Logout the current user
   */
  async function logout(): Promise<void> {
    loading.value = true;
    error.value = null;
    stopRefreshTimer();

    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore errors on logout
    } finally {
      user.value = null;
      sessionExpiresAt.value = null;
      loading.value = false;
    }
  }

  /**
   * Fetch current user data
   */
  async function fetchUser(): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.get<{ data: User }>('/auth/me');
      user.value = response.data.data;
    } catch {
      user.value = null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Check if user is authenticated (silent check)
   */
  async function checkAuth(): Promise<boolean> {
    try {
      const response = await api.get<{ data: User }>('/auth/me');
      user.value = response.data.data;
      initSession();
      return true;
    } catch {
      user.value = null;
      stopRefreshTimer();
      return false;
    }
  }

  /**
   * Clear error state
   */
  function clearError(): void {
    error.value = null;
    retryAfter.value = null;
  }

  /**
   * Refresh the session silently
   * NFR-P10: Refresh token transparent sans interruption UX
   */
  async function refreshSession(): Promise<boolean> {
    if (!user.value) return false;

    try {
      const response = await api.post<{
        data: {
          session_lifetime_minutes: number;
          expires_at: string;
          refresh_recommended_at: string;
        };
        message: string;
      }>('/auth/refresh');

      sessionExpiresAt.value = response.data.data.expires_at;
      return true;
    } catch {
      // Session may have expired, user needs to re-login
      return false;
    }
  }

  /**
   * Start automatic session refresh timer
   */
  function startRefreshTimer(): void {
    stopRefreshTimer();
    refreshTimer = setInterval(async () => {
      const refreshed = await refreshSession();
      if (!refreshed && user.value) {
        // Session expired, trigger re-authentication
        user.value = null;
        stopRefreshTimer();
      }
    }, REFRESH_INTERVAL_MS);
  }

  /**
   * Stop the refresh timer
   */
  function stopRefreshTimer(): void {
    if (refreshTimer) {
      clearInterval(refreshTimer);
      refreshTimer = null;
    }
  }

  /**
   * Initialize session management after authentication
   */
  function initSession(): void {
    startRefreshTimer();
    // Initial refresh to get session info
    refreshSession();
  }

  return {
    // State
    user,
    loading,
    error,
    retryAfter,
    sessionExpiresAt,
    // Getters
    isAuthenticated,
    fullName,
    userRole,
    isAdmin,
    isResponsableSportif,
    // Actions
    hasPermission,
    login,
    logout,
    fetchUser,
    checkAuth,
    clearError,
    refreshSession,
    stopRefreshTimer,
  };
});
